import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare } from 'lucide-react';
import api from '../utils/api';

var WELCOME_MSG = {
  role: 'assistant',
  text: 'Ciao! Sono il tuo assistente. Posso rispondere a domande sui tuoi documenti caricati in Knowledge Base.'
};

export default function ChatWidget() {
  var messagesRef = useRef(null);
  var inputRef = useRef(null);
  var _open = useState(false);
  var open = _open[0];
  var setOpen = _open[1];
  var _messages = useState([WELCOME_MSG]);
  var messages = _messages[0];
  var setMessages = _messages[1];
  var _loading = useState(false);
  var loading = _loading[0];
  var setLoading = _loading[1];
  var _input = useState('');
  var inputVal = _input[0];
  var setInputVal = _input[1];

  useEffect(function() {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(function() {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  function handleToggle() {
    if (open) {
      setOpen(false);
    } else {
      setMessages([WELCOME_MSG]);
      setOpen(true);
    }
  }

  function handleSend() {
    var text = inputVal.trim();
    if (!text || loading) return;
    setInputVal('');
    if (inputRef.current) inputRef.current.style.height = '36px';
    var userMsg = { role: 'user', text: text };
    setMessages(function(prev) { return prev.concat([userMsg]); });
    setLoading(true);

    api.post('/ai/chat/message/', { message: text })
      .then(function(res) {
        var reply = { role: 'assistant', text: res.data.reply };
        setMessages(function(prev) { return prev.concat([reply]); });
      })
      .catch(function(err) {
        var status = err.response && err.response.status;
        var data = err.response && err.response.data;
        var errMsg;
        if (status === 429 && data && data.upgrade) {
          errMsg = { role: 'assistant', text: 'Hai raggiunto il limite mensile del tuo piano.', upgrade: true };
        } else if (status === 403 && data && data.detail) {
          // ARC-029: 403 TenantMancante (tenant assente, corpo {detail}) != limite piano ({error}).
          errMsg = { role: 'assistant', text: 'Si è verificato un errore. Riprova tra qualche istante.' };
        } else if (status === 403) {
          errMsg = { role: 'assistant', text: 'Funzione non disponibile per il tuo piano.' };
        } else {
          errMsg = { role: 'assistant', text: 'Si \u00e8 verificato un errore. Riprova tra qualche istante.' };
        }
        setMessages(function(prev) { return prev.concat([errMsg]); });
      })
      .finally(function() {
        setLoading(false);
      });
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  // Floating button
  var btnStyle = {
    position: 'fixed',
    bottom: 24,
    right: 24,
    zIndex: 1000,
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: 'var(--brand-gradient)',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 6px 18px rgba(35,38,47,0.18)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.2s',
  };

  // Chat panel
  var panelStyle = {
    position: 'fixed',
    bottom: 88,
    right: 24,
    width: 360,
    maxWidth: 'calc(100vw - 48px)',
    maxHeight: 520,
    borderRadius: 'var(--radius-card)',
    background: 'rgb(var(--surface))',
    boxShadow: '0 12px 40px rgba(35,38,47,0.14)',
    border: '1px solid var(--ds-border)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 1000,
  };

  if (!open) {
    return React.createElement('button', {
      onClick: handleToggle,
      style: btnStyle,
      title: 'Assistente mAIstro',
    },
      React.createElement(MessageSquare, { size: 24, color: '#fff', strokeWidth: 2 })
    );
  }

  return React.createElement('div', null,
    // Floating button (open state — shows X)
    React.createElement('button', {
      onClick: handleToggle,
      style: btnStyle,
    },
      React.createElement('svg', { width: 24, height: 24, fill: 'none', stroke: '#fff', strokeWidth: 2.5, viewBox: '0 0 24 24' },
        React.createElement('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
        React.createElement('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
      )
    ),

    // Chat panel
    React.createElement('div', { style: panelStyle },
      // Header
      React.createElement('div', {
        style: {
          background: 'var(--brand)',
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }
      },
        React.createElement('div', {
          style: {
            width: 36, height: 36, background: 'rgba(255,255,255,0.3)',
            borderRadius: '50%', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontWeight: 900, fontStyle: 'italic',
            color: '#fff', fontSize: 15,
          }
        }, 'm'),
        React.createElement('div', { style: { flex: 1 } },
          React.createElement('div', { style: { fontWeight: 700, fontSize: 14, color: '#fff' } }, 'Assistente mAIstro'),
          React.createElement('div', { style: { fontSize: 11, color: '#fff', opacity: 0.7 } }, 'Knowledge Base AI')
        ),
        React.createElement('button', {
          onClick: handleToggle,
          style: {
            background: 'rgba(255,255,255,0.25)', border: 'none', width: 28,
            height: 28, borderRadius: '50%', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }
        },
          React.createElement('svg', { width: 14, height: 14, fill: 'none', stroke: '#fff', strokeWidth: 2.5, viewBox: '0 0 24 24' },
            React.createElement('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
            React.createElement('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
          )
        )
      ),

      // Messages area
      React.createElement('div', {
        ref: messagesRef,
        style: {
          flex: 1, overflowY: 'auto', padding: 16,
          display: 'flex', flexDirection: 'column', gap: 10,
          background: 'var(--chip)', minHeight: 280, maxHeight: 340,
        }
      },
        messages.map(function(msg, i) {
          var isUser = msg.role === 'user';
          return React.createElement('div', {
            key: i,
            style: {
              display: 'flex', gap: 8, alignItems: 'flex-end',
              flexDirection: isUser ? 'row-reverse' : 'row',
            }
          },
            !isUser && React.createElement('div', {
              style: {
                width: 28, height: 28,
                background: 'var(--brand)',
                borderRadius: '50%', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontWeight: 900, fontStyle: 'italic',
                fontSize: 11, color: '#fff', flexShrink: 0,
              }
            }, 'm'),
            React.createElement('div', null,
              React.createElement('div', {
                style: {
                  background: isUser ? 'var(--brand)' : '#fff',
                  color: isUser ? '#fff' : 'rgb(var(--text))',
                  borderRadius: isUser ? '14px 14px 2px 14px' : '14px 14px 14px 2px',
                  padding: '10px 14px',
                  maxWidth: 250,
                  fontSize: 13,
                  lineHeight: 1.5,
                  boxShadow: isUser ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
                  border: isUser ? 'none' : '1px solid var(--ds-border)',
                  whiteSpace: 'pre-wrap',
                }
              }, msg.text),
              msg.upgrade && React.createElement('a', {
                href: '/impostazioni',
                style: {
                  display: 'inline-block', marginTop: 6, fontSize: 12,
                  fontWeight: 700, color: 'var(--brand)', textDecoration: 'none',
                  padding: '5px 14px', border: '1px solid #D5E6DE',
                  borderRadius: 20, background: '#fff',
                }
              }, 'Upgrade \u2192')
            )
          );
        }),
        loading && React.createElement('div', {
          style: {
            display: 'flex', gap: 8, alignItems: 'flex-end',
          }
        },
          React.createElement('div', {
            style: {
              width: 28, height: 28,
              background: 'var(--brand)',
              borderRadius: '50%', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontWeight: 900, fontStyle: 'italic',
              fontSize: 11, color: '#fff', flexShrink: 0,
            }
          }, 'm'),
          React.createElement('div', {
            style: {
              background: '#fff', borderRadius: '14px 14px 14px 2px',
              padding: '10px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              border: '1px solid var(--ds-border)',
              display: 'flex', gap: 4, alignItems: 'center',
            }
          },
            React.createElement('span', { className: 'chat-dot chat-dot-1', style: { width: 6, height: 6, borderRadius: '50%', background: 'rgb(var(--muted))', animation: 'chatPulse 1.4s ease-in-out infinite' } }),
            React.createElement('span', { className: 'chat-dot chat-dot-2', style: { width: 6, height: 6, borderRadius: '50%', background: 'rgb(var(--muted))', animation: 'chatPulse 1.4s ease-in-out 0.2s infinite' } }),
            React.createElement('span', { className: 'chat-dot chat-dot-3', style: { width: 6, height: 6, borderRadius: '50%', background: 'rgb(var(--muted))', animation: 'chatPulse 1.4s ease-in-out 0.4s infinite' } })
          )
        )
      ),

      // Input area
      React.createElement('div', {
        style: {
          padding: '10px 14px', background: 'rgb(var(--surface))',
          borderTop: '1px solid var(--ds-border)',
          display: 'flex', gap: 8, alignItems: 'flex-end',
        }
      },
        React.createElement('textarea', {
          ref: inputRef,
          placeholder: 'Scrivi una domanda...',
          value: inputVal,
          rows: 1,
          onChange: function(e) {
            setInputVal(e.target.value);
            e.target.style.height = 'auto';
            var newHeight = Math.min(e.target.scrollHeight, 120);
            e.target.style.height = newHeight + 'px';
          },
          onKeyDown: handleKeyDown,
          onFocus: function(e) { e.target.style.borderColor = 'var(--brand)'; },
          onBlur: function(e) { e.target.style.borderColor = 'var(--ds-border)'; },
          disabled: loading,
          style: {
            flex: 1, border: '1px solid var(--ds-border)',
            borderRadius: 'var(--radius-base)', padding: '8px 14px', fontSize: 13,
            fontFamily: 'Inter, sans-serif', outline: 'none',
            color: 'rgb(var(--text))', background: 'rgb(var(--bg))',
            resize: 'none', overflow: 'hidden',
            minHeight: 36, maxHeight: 120, lineHeight: 1.4,
          },
        }),
        React.createElement('button', {
          onClick: handleSend,
          disabled: loading || !inputVal.trim(),
          style: {
            width: 34, height: 34, borderRadius: '50%',
            background: loading || !inputVal.trim() ? 'rgb(var(--muted))' : 'var(--brand)',
            border: 'none', cursor: loading ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, transition: 'background 0.15s',
          }
        },
          React.createElement('svg', { width: 16, height: 16, fill: 'none', stroke: '#fff', strokeWidth: 2, viewBox: '0 0 24 24' },
            React.createElement('line', { x1: 22, y1: 2, x2: 11, y2: 13 }),
            React.createElement('polygon', { points: '22 2 15 22 11 13 2 9 22 2' })
          )
        )
      )
    )
  );
}
